import {sendChat} from './api.js';

/**
 * Deterministic parser for free-text business descriptions,
 * with optional AI fallback via sendChat.
 */
export function parseBusinessDescriptionDeterministic(text = '') {
  if (!text.trim()) return {};

  const result = {};
  const clean = text.trim();

  // 1. Business Name
  const nameMatch = clean.match(/(?:we are|i run|company called|business called|name is|welcome to)\s+([A-Z][A-Za-z0-9\s&'-]+?)(?:,|\.|\s+an?\s|\s+a\s|\s+the\s|\s+based\s|\s+specializing)/i)
    || clean.match(/^([A-Z][A-Za-z0-9\s&'-]+?)\s+is\s+an?/);
  if (nameMatch && nameMatch[1]) {
    result.name = nameMatch[1].trim();
  }

  // 2. Industry
  const industryMatch = clean.match(/(?:an?|in the|specializing in|focuses on|industry:\s*)([a-z0-9\s-]+?)(?:industry|sector|business|studio|brand|company|space)/i)
    || clean.match(/(?:sustainable|organic|ecommerce|b2b|saas|retail|hospitality|consulting|design|financial|healthcare)\s+[a-z\s]+/i);
  if (industryMatch) {
    result.industry = (industryMatch[1] || industryMatch[0]).trim();
  }

  // 3. Audience
  const audienceMatch = clean.match(/(?:for|targeting|audience:\s*|serving|customers are|clients are)\s+([a-z0-9\s,-]+?)(?:\.|\n|our goal|we sound|we avoid|goals)/i);
  if (audienceMatch && audienceMatch[1]) {
    result.audience = audienceMatch[1].trim();
  }

  // 4. Goals
  const goalsMatch = clean.match(/(?:our goal is|aiming to|goals?:\s*|focusing on|mission is|looking to)\s+([a-z0-9\s,-]+?)(?:\.|\n|we sound|we avoid|voice)/i);
  if (goalsMatch && goalsMatch[1]) {
    result.goals = goalsMatch[1].trim();
  }

  // 5. Brand Voice
  const voiceMatch = clean.match(/(?:voice is|sound|tone:\s*|we speak|brand voice:\s*)\s+([a-z0-9\s,-]+?)(?:\.|\n|and we avoid|we avoid|avoid)/i);
  if (voiceMatch && voiceMatch[1]) {
    result.voice = voiceMatch[1].trim();
  }

  // 6. Avoid
  const avoidMatch = clean.match(/(?:we avoid|never say|do not use|avoid:\s*|no)\s+([a-z0-9\s,-]+?)(?:\.|\n|$)/i);
  if (avoidMatch && avoidMatch[1]) {
    result.avoid = avoidMatch[1].trim();
  }

  return result;
}

export async function parseBusinessDescription(text) {
  const deterministic = parseBusinessDescriptionDeterministic(text);

  // If at least 3 key fields were found, return deterministic result immediately
  const fieldCount = Object.keys(deterministic).length;
  if (fieldCount >= 3) {
    return deterministic;
  }

  // AI Fallback via sendChat sandbox turn
  try {
    const prompt = `Extract structured business fields from this description into valid JSON with keys "name", "industry", "audience", "goals", "voice", "avoid". Return JSON only. Description: "${text}"`;
    const reply = await sendChat({
      module: 'introduction',
      botId: 'bot_bm',
      text: prompt,
      sandbox: true
    });

    if (reply?.text) {
      const jsonMatch = reply.text.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        return {...deterministic, ...parsed};
      }
    }
  } catch {
    // Fall back to whatever deterministic parser found
  }

  return deterministic;
}
